import { z } from 'zod/v4';
import {
  onboardingRoleSchema,
  teamSizeSchema,
  industrySchema,
  supportedLocaleSchema,
} from './onboarding';

// POST /api/v1/demo-data/seed
export const demoDataSeedRequestSchema = z.object({
  locale: supportedLocaleSchema.default('en'),
  // Optional personalisation inputs from the new onboarding flow.
  // When present the resolver picks a dataset tailored to the user;
  // otherwise it falls back to the generic `default/default.json`.
  role: onboardingRoleSchema.optional(),
  industry: industrySchema.optional(),
});

export const demoDataSeedResponseSchema = z.object({
  success: z.boolean(),
  recordCount: z.number(),
});

// Canonical demo dataset keys (`IndustryKey` in `@aica/demo-data`). NOT the UI
// `industrySchema` above: that one is what the user picks in the wizard, this
// one is which dataset got seeded.
export const demoDatasetKeySchema = z.enum([
  'generic',
  'real_estate',
  'medical',
  'ecommerce',
  'b2b_sales',
]);

// GET /api/v1/demo-data/status  (and GET /api/v1/embedded/demo-data/status)
// Shape = exactly what both routes return: `routes/demo-data.ts` and
// `routes/embedded/demo-data.ts` in edge-api.
export const demoDataStatusResponseSchema = z.object({
  hasDemoData: z.boolean(),
  recordCount: z.number().int(),
  // True once the org has at least one REAL (non-demo) record, whatever the
  // viewer's own scope. Gates demo auto-clear and demo mode: demo stays until
  // the user's own data actually arrives, rather than the moment a source is
  // merely connected (which for Bitrix24 embedded installs is day one).
  //
  // Optional on purpose: edge-api and ui-worker deploy and roll back
  // independently, and a response from an edge-api older than this field
  // omits it. A required field would make the UI's strict parse throw an
  // ApiContractError on every status read after a rollback. Absent means
  // "unknown" - consumers fall back to other evidence, never to `false`.
  hasRealData: z.boolean().optional(),
  industry: demoDatasetKeySchema.nullable(),
  locale: supportedLocaleSchema.nullable(),
  ahaCallId: z.string().nullable(),
  seededAt: z.string().nullable(),
});

// DELETE /api/v1/demo-data/clear
export const demoDataClearResponseSchema = z.object({
  success: z.boolean(),
});

// POST /api/v1/embedded/demo-data/onboard
// Embedded onboarding survey: collects industry/role/team-size (the standalone
// SaaS wizard's Quick Context) and re-seeds the demo dataset to match. `locale`
// drives the seeded language. `industry` is a UI industry value; the backend
// maps it to a canonical dataset key via `mapUiIndustryToKey`.
export const demoDataOnboardRequestSchema = z.object({
  industry: industrySchema,
  role: onboardingRoleSchema,
  teamSize: teamSizeSchema,
  locale: supportedLocaleSchema.default('en'),
  /**
   * Название компании из того же шага опроса (`QuickContextStep`).
   *
   * Необязательное, хотя форма его требует: у клиента может быть открыт
   * старый бандл SPA, который поле ещё не шлёт, и жёсткое требование
   * превратило бы его онбординг в 400 без объяснения. Границы — как в форме
   * (min 2 / max 100), чтобы валидация не расходилась между шагом и приёмом.
   *
   * Организацию переименовывает только машинное имя вида
   * `<домен> (Bitrix24)`; см. `routes/embedded/demo-data.ts`.
   */
  organizationName: z.string().trim().min(2).max(100).optional(),
});

export const demoDataOnboardResponseSchema = z.object({
  success: z.boolean(),
});

// Types
export type DemoDataSeedRequest = z.infer<typeof demoDataSeedRequestSchema>;
export type DemoDataSeedResponse = z.infer<typeof demoDataSeedResponseSchema>;
export type DemoDataStatusResponse = z.infer<typeof demoDataStatusResponseSchema>;
export type DemoDataClearResponse = z.infer<typeof demoDataClearResponseSchema>;
export type DemoDataOnboardRequest = z.infer<typeof demoDataOnboardRequestSchema>;
export type DemoDataOnboardResponse = z.infer<typeof demoDataOnboardResponseSchema>;
